import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SmallCandidateCard from "../../../components/candidates/small_candidate";
import ShortlistCard from "../../../components/candidates/new_shortlists";
import RequestRecsModal from "../../../components/modals/RequestRecsModal";
import KeenToSeeMoreModal from "../../../components/modals/KeenToSeeMoreModal";
import { Navigation } from "swiper/modules";
import Swiper from "swiper";
import "./swiper.min.css";
import { useShortlisted } from "../../../provider/ShortlistedContext";

Swiper.use([Navigation]);

function Shortlists() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const swiperInstances = useRef([]);
  const navigate = useNavigate();
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [showRequestRecsModal, setShowRequestRecsModal] = useState(false);
  const [requestingRecs, setRequestingRecs] = useState(false);
  const [showKeenModal, setShowKeenModal] = useState(false);
  const [keenModalInfo, setKeenModalInfo] = useState(null);
  const [roles, setRoles] = useState([]);
  const [shortlistRationales, setShortlistRationales] = useState({}); // Store rationales by roleId

  const { shortlistedCount, setShortlistedCount } = useShortlisted();

  const handleShortlist = (roleURLId) => {
    navigate(`/shortlist?roleRequestId=${roleURLId}`);
    window.location.reload();
  };

  const transformRoles = (roles) => {
    const roleSet = new Set(roles);
    if (roleSet.size === 1) {
      if (roleSet.has("Full Stack Developer")) return ["Full Stack"];
      if (roleSet.has("Front End Developer")) return ["Front End"];
      if (roleSet.has("Back End Developer")) return ["Back End"];
    }
    if (
      roleSet.has("Front End Developer") &&
      roleSet.has("Back End Developer")
    ) {
      return ["Full Stack"];
    }
    if (roleSet.has("Full Stack Developer")) {
      return ["Full Stack"];
    }
    return roles;
  };

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/roles/fetchRoles", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const rolesData = response.data.roles;
        
        // Sort roles by "Shortlist last changed" in descending order (most recent first)
        const sortedRolesData = rolesData.sort((a, b) => {
          const dateA = a["Shortlist last changed"] ? new Date(a["Shortlist last changed"]) : new Date(0);
          const dateB = b["Shortlist last changed"] ? new Date(b["Shortlist last changed"]) : new Date(0);
          return dateB - dateA; // Descending order (most recent first)
        });
        
        setRoles(sortedRolesData);

        let shortlistedCandidatesByRole = {};

        sortedRolesData.forEach((role) => {
          if (role["Shortlisted Candidates"]) {
            const roleTitle = role["Role Title"];
            const roleURLId = role["URL ID"];
            const roleId = role["id"];

            if (!shortlistedCandidatesByRole[roleTitle]) {
              shortlistedCandidatesByRole[roleTitle] = {
                roleURLId: roleURLId,
                roleId: roleId,
                candidates: [],
              };
            }
            shortlistedCandidatesByRole[roleTitle].candidates =
              shortlistedCandidatesByRole[roleTitle].candidates.concat(
                role["Shortlisted Candidates"]
              );
          }
        });

        const roleNames = Object.keys(shortlistedCandidatesByRole);
        
        if (roleNames.length > 0) {
          for (const roleName of roleNames) {
            const roleInfo = shortlistedCandidatesByRole[roleName];
            
            try {
              await fetchCandidates(
                roleInfo.candidates,
                roleName,
                roleInfo.roleURLId,
                roleInfo.roleId
              );
              
              // Fetch rationales for this role
              await fetchShortlistRationales(roleInfo.roleId);
            } catch (candidateError) {
              console.error(`Failed to fetch candidates for role ${roleName}:`, candidateError);
            }
          }
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      }
    };

    const fetchCandidates = async (
      candidateIds,
      roleName,
      roleURLId,
      roleId
    ) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/candidates/fetchCandidates", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            ids: candidateIds.join(","),
          },
        });

        const candidates = response.data.candidates;

        // Remove duplicates from candidate IDs first
        const uniqueCandidateIds = [...new Set(candidateIds)];
        
        const reversedCandidateIds = [...uniqueCandidateIds].reverse();
        const sortedCandidates = reversedCandidateIds
          .map(id => candidates.find(c => c.id === id))
          .filter(c => c);

        const transformedData = sortedCandidates.map((candidate, index) => {
          try {
            // Safely extract fields with proper null checking
            const mostRecentCohort =
              candidate.fields?.["Most recent cohort"] || "";
            
            // Safe access to Stream (from CRM) - handle both undefined and non-array cases
            const streamFromCRMArray = candidate.fields?.["Stream (from CRM)"];
            const streamFromCRM = Array.isArray(streamFromCRMArray) && streamFromCRMArray.length > 0 
              ? streamFromCRMArray[0] 
              : "";

            let stream = streamFromCRM;

            if (!streamFromCRM) {
              if (/^JS\d+$/.test(mostRecentCohort)) {
                stream = "Generalist";
              } else if (/^JSS\d+$/.test(mostRecentCohort)) {
                stream = "Sales";
              } else if (/^JSWE\d+$/.test(mostRecentCohort)) {
                stream = "Software Engineering";
              } else if (/^JSM\d+$/.test(mostRecentCohort)) {
                stream = "Marketing";
              }
            }

            let sweRoles = candidate.fields?.["SWE relevant roles"] || "";
            if (sweRoles) {
              sweRoles = Array.isArray(sweRoles)
                ? sweRoles[0].split(",").map((role) => role.trim())
                : sweRoles.split(",").map((role) => role.trim());
              sweRoles = transformRoles(sweRoles);
            } else {
              sweRoles = [];
            }

            // Safe access to Featured status
            const featuredStatusArray = candidate.fields?.["Featured status"];
            const featured = Array.isArray(featuredStatusArray) && featuredStatusArray.length > 0 
              ? featuredStatusArray[0] 
              : "";

            return {
              id: candidate.id || "",
              url_id: candidate.fields?.["ID"] || "",
              featured: featured,
              name: candidate.fields?.["Name"] || "",
            cv: candidate.fields["CV RAW"] || "",
            video: candidate.fields["Video intro (new)"] || "",
            linkedin: candidate.fields["LinkedIn RAW"] || "",
            industry_exp: candidate.fields["Industry experience"] || undefined,
            lastName: candidate.fields["Surname"] || "",
            roles: candidate.fields["Generalist roles suitable for copy"] || "",
            email: candidate.fields["Email"] || "",
            working_style:
              candidate.fields["Preferred working style copy"] || "",
            github: candidate.fields["Github RAW"] || "",
            languages: candidate.fields["Languages copy"] || "",
            time_coding: candidate.fields["Time spent coding"] || "",
            description: candidate.fields["Top achievement"] || "",
            mostRecentCohort: candidate.fields["Most recent cohort"] || "",
            recentTarget: candidate.fields?.["Recent target achieved"] || "",
            image: candidate.fields?.Photo && Array.isArray(candidate.fields.Photo) && candidate.fields.Photo.length > 0
              ? candidate.fields.Photo[0]?.url || null
              : null,
            stream: stream,
            role: roleName,
            roleURLId: roleURLId,
            roleId: roleId,
            minimum_salary: candidate.fields["Minimum salary"] || "",
            ideal_salary: candidate.fields["Ideal salary"] || "",
            work_experience_1_role:
              candidate.fields["(1) Work Experience Role"] || "",
            work_experience_1_company:
              candidate.fields["(1) Work Experience Company"] || "",
            work_experience_1_description:
              candidate.fields["(1) Work Experience Description"] || "",
              work_experience_1_duration: {
                years: candidate.fields["(1) Time spent in work experience YRS"] || 0,
                months: candidate.fields["(1) Time spent in work experience MOS"] || 0
              },
            work_experience_2_role:
              candidate.fields["(2) Work Experience Role"] || "",
            work_experience_2_company:
              candidate.fields["(2) Work Experience Company"] || "",
            work_experience_2_description:
              candidate.fields["(2) Work Experience Description"] || "",
              work_experience_2_duration: {
                years: candidate.fields["(2) Time spent in work experience YRS"] || 0,
                months: candidate.fields["(2) Time spent in work experience MOS"] || 0
              },
            undergrad_degree_type:
              candidate.fields["Undergrad degree type"] || "",
            undergrad_degree_subject:
              candidate.fields["Undergrad subject"] || "",
            undergrad_university:
              candidate.fields["Undergrad university"] || "",
            undergrad_graduation_year:
              candidate.fields["Undergrad graduation year"] || "",
            undergrad_result: candidate.fields["Undergrad result"] || "",
            postgrad_degree_type:
              candidate.fields["Postgrad degree type"] || "",
            postgrad_degree_subject: candidate.fields["Postgrad subject"] || "",
            postgrad_university: candidate.fields["Postgrad university"] || "",
            postgrad_graduation_year:
              candidate.fields["Postgrad graduation year"] || "",
            postgrad_result: candidate.fields["Postgrad result"] || "",
            right_to_work: candidate.fields?.["RTW doc"] || "",
            "Jumpstart view (Sales)": candidate.fields?.["Jumpstart view (Sales)"] || "",
          };
          } catch (candidateError) {
            console.error(`Error processing candidate ${candidate.id} for role ${roleName}:`, candidateError);
            
            // Return a minimal candidate object to prevent the entire batch from failing
            return {
              id: candidate.id || `error-candidate-${index}`,
              url_id: candidate.fields?.["ID"] || "",
              featured: "",
              name: candidate.fields?.["Name"] || "Unknown Candidate",
              cv: "",
              video: "",
              linkedin: "",
              industry_exp: undefined,
              lastName: candidate.fields?.["Surname"] || "",
              roles: "",
              email: candidate.fields?.["Email"] || "",
              working_style: "",
              github: "",
              languages: "",
              time_coding: "",
              description: "",
              mostRecentCohort: "",
              recentTarget: "",
              image: null,
              stream: "",
              role: roleName,
              roleURLId: roleURLId,
              roleId: roleId,
              minimum_salary: "",
              ideal_salary: "",
              work_experience_1_role: "",
              work_experience_1_company: "",
              work_experience_1_description: "",
              work_experience_1_duration: { years: 0, months: 0 },
              work_experience_2_role: "",
              work_experience_2_company: "",
              work_experience_2_description: "",
              work_experience_2_duration: { years: 0, months: 0 },
              undergrad_degree_type: "",
              undergrad_degree_subject: "",
              undergrad_university: "",
              undergrad_graduation_year: "",
              undergrad_result: "",
              postgrad_degree_type: "",
              postgrad_degree_subject: "",
              postgrad_university: "",
              postgrad_graduation_year: "",
              postgrad_result: "",
              right_to_work: "",
              "Jumpstart view (Sales)": "",
            };
          }
        });

        setData((prevData) => ({
          ...prevData,
          [roleName]: (prevData[roleName] || []).concat(transformedData),
        }));

        setSelectedCandidates((prev) => ({
          ...prev,
          [roleName]: [...new Set(transformedData.map((candidate) => candidate.id))], // Remove duplicates
        }));
      } catch (err) {
        throw err;
      }
    };

    fetchRole();
  }, []);

  // Function to fetch shortlist rationales for a specific role
  const fetchShortlistRationales = async (roleId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/candidates/fetchShortlistRationales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          roleId: roleId,
        },
      });

      if (response.data.rationales) {
        setShortlistRationales(prev => ({
          ...prev,
          [roleId]: response.data.rationales
        }));
      } else {
        // Add test data to verify UI is working
        setShortlistRationales(prev => ({
          ...prev,
          [roleId]: {
            // Use the first candidate in the role for testing
            'recT80QZPcBmHjKd6': {
              rationale: "This candidate has excellent communication skills and a strong background in JavaScript development. Their experience at startups makes them a great culture fit.",
              matchScore: "Excellent Match",
              addedBy: "Martha",
              addedAt: new Date().toISOString()
            }
          }
        }));
      }
    } catch (err) {
      console.error(`Error fetching shortlist rationales for role ${roleId}:`, err);
      // Don't throw error - rationales are optional enhancement
    }
  };

  const handleRequestInterview = async (roleURLId, role) => {
    if (!selectedCandidates[role] || selectedCandidates[role].length === 0) {
      console.error("No candidates selected for interview");
      return;
    }

    const updatedData = {
      fields: {
        "Selected Candidates": selectedCandidates[role],
        "Shortlisted Candidates": data[role].map(candidate => candidate.id)
      },
      roleId: data[role][0].roleId,
    };

    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/roles/updateRoleData/`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      navigate(`/new-request-interview?roleRequestId=${roleURLId}`);
    } catch (err) {
      console.error(
        "Error updating selected candidates:",
        err.response?.data?.error || err.message
      );
    }
  };

  const handleRequestMoreRecs = async (roleId) => {
    if (!roleId) {
      console.error("No roleId provided to request recommendations");
      return;
    }
    
    setRequestingRecs(true);
    
    try {
      const token = localStorage.getItem("token");
      
      const currentDateTime = new Date().toISOString();
      
      const updatedData = {
        fields: {
          "Request recs": true,
          "Request recs time": currentDateTime
        },
        roleId: roleId,
      };

      await axios.patch("/api/roles/updateRoleData", updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setShowRequestRecsModal(true);
    } catch (err) {
      console.error(
        "Error requesting more recommendations:",
        err.response?.data?.error || err.message
      );
    } finally {
      setRequestingRecs(false);
    }
  };

  const [removing, setRemoving] = useState(null);

  const handleRemoveCandidate = async (role, candidateId) => {
    if (!data[role]) {
      console.error(`Role ${role} not found in data`);
      return;
    }

    setRemoving(candidateId);

    const currentShortlistedCandidates = data[role] || [];
    const updatedShortlistedCandidates = currentShortlistedCandidates
      .filter((candidate) => candidate.id !== candidateId)
      .map((candidate) => candidate.id);
    
    // Remove duplicates to prevent Airtable 422 error
    const uniqueUpdatedShortlistedCandidates = [...new Set(updatedShortlistedCandidates)];
    
    const isLastCandidate = uniqueUpdatedShortlistedCandidates.length === 0;
    const roleId = data[role][0]?.roleId;

    // Also remove duplicates from selected candidates
    const uniqueSelectedCandidates = [...new Set(selectedCandidates[role]?.filter(id => id !== candidateId) || [])];

    const updatedData = {
      fields: {
        "Shortlisted Candidates": uniqueUpdatedShortlistedCandidates,
        "Selected Candidates": uniqueSelectedCandidates
      },
      roleId: roleId,
    };

    // Validate the data before sending
    if (!roleId) {
      console.error("No roleId found - cannot update role");
      alert("Error: Could not find role ID. Please refresh the page and try again.");
      return;
    }
    
    if (!uniqueUpdatedShortlistedCandidates || !Array.isArray(uniqueUpdatedShortlistedCandidates)) {
      console.error("Invalid shortlisted candidates array");
      alert("Error: Invalid candidate data. Please refresh the page and try again.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        console.error("No authentication token found");
        alert("Error: You are not logged in. Please log in and try again.");
        return;
      }
      
      await axios.patch("/api/roles/updateRoleData", updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setData((prevData) => {
        const updatedRoleCandidates = prevData[role].filter(
          (candidate) => candidate.id !== candidateId
        );

        if (updatedRoleCandidates.length === 0) {
          const { [role]: _, ...remainingData } = prevData;
          return remainingData;
        }

        return {
          ...prevData,
          [role]: updatedRoleCandidates,
        };
      });

      setSelectedCandidates((prev) => ({
        ...prev,
        [role]: prev[role]?.filter(id => id !== candidateId) || []
      }));

      const newShortlistedCount = shortlistedCount - 1;
      setShortlistedCount(newShortlistedCount);

      if (isLastCandidate) {
        setKeenModalInfo({ roleName: role, roleId: roleId });
        setShowKeenModal(true);
      }
    } catch (err) {
      console.error("API call failed:", err);
      
      // Alert the user about the detailed error
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown server error';
      alert(`Failed to remove candidate: ${errorMessage}`);
    } finally {
      setRemoving(null);
    }
  };

  const handleAddSelectedCandidate = (role, candidateId) => {
    setSelectedCandidates((prev) => ({
      ...prev,
      [role]: [...new Set([...(prev[role] || []), candidateId])], // Prevent duplicates
    }));
  };

  const handleRemoveSelectedCandidate = (role, candidateId) => {
    setSelectedCandidates((prev) => ({
      ...prev,
      [role]: prev[role].filter((id) => id !== candidateId),
    }));
  };

  const handleSelectAll = (role) => {
    if (!data[role]) return;

    const allCandidatesIds = [...new Set(data[role].map((candidate) => candidate.id))]; // Remove duplicates
    setSelectedCandidates((prev) => ({
      ...prev,
      [role]: allCandidatesIds,
    }));
  };

  const handleDeselectAll = (role) => {
    setSelectedCandidates((prev) => ({
      ...prev,
      [role]: [],
    }));
  };

  const areAllCandidatesSelected = (role) => {
    return (
      selectedCandidates[role]?.length === data[role]?.length &&
      data[role]?.length > 0
    );
  };

  const handleToggleSelectAll = (role) => {
    if (areAllCandidatesSelected(role)) {
      handleDeselectAll(role);
    } else {
      handleSelectAll(role);
    }
  };

  const handleShortlistCandidate = async (role, candidateId) => {
    try {
      const currentShortlistedCandidates = role["Shortlisted Candidates"] || [];
      const currentCandidatesShortlistedBefore = role["Candidates Shortlisted Before"] || [];
      
      const updatedShortlistedCandidates = [
        ...new Set([...currentShortlistedCandidates, candidateId]),
      ];
      
      // Add to historical tracking field (no duplicates)
      const updatedCandidatesShortlistedBefore = [
        ...new Set([...currentCandidatesShortlistedBefore, candidateId]),
      ];

      const updatedData = {
        fields: {
          "Shortlisted Candidates": updatedShortlistedCandidates,
          "Candidates Shortlisted Before": updatedCandidatesShortlistedBefore,
        },
        roleId: role.id,
      };

      const token = localStorage.getItem("token");
      await axios.patch("/api/roles/updateRoleData", updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const newShortlistedCount = shortlistedCount + 1;
      setShortlistedCount(newShortlistedCount);

      console.log(`Candidate ${candidateId} added to ${role["Role Title"]} shortlist`);
    } catch (err) {
      console.error("Error adding candidate to shortlist:", err);
    }
  };

  return (
    <div className="flex pl-4 md:pl-56 flex-col h-screen items-center bg-white pr-2 md:pr-4 overflow-x-hidden">
      <div className="ml-auto w-full flex flex-col h-full bg-white">

        <div className="bg-white pl-4 md:pl-10 pt-4 md:pt-6 pb-3 md:pb-4 fixed top-18 z-30 md:z-50 w-full border-b shadow-sm">
          <div className="flex justify-left items-center">
            <div>
              <h2 className="text-2xl md:text-3xl">Shortlists</h2>
              <div className="mt-1 md:mt-2"></div>
            </div>
          </div>
        </div>
        <div className="pl-4 md:pl-10 pt-20 md:pt-28 bg-white">
          {loading ? (
            <>
              <div className="h-6 md:h-8 w-32 md:w-40 bg-gray-200 ml-2 md:ml-4 mt-4 md:mt-6 rounded-full mb-2.5"></div>
              <div className="grid pt-2 grid-cols-1 mr-2 md:mr-5 ml-2 md:ml-4 md:grid-cols-2 lg:grid-cols-3">
                {Array(2)
                  .fill(0)
                  .map((_, index) => (
                    <div
                      role="status"
                      className="w-full md:w-[375px] p-3 md:p-4 rounded-lg animate-pulse bg-white shadow-md border border-gray-200"
                      key={index}
                    >
                      <div className="flex items-center justify-center w-full h-60 md:h-72 mb-3 md:mb-4 bg-gray-300 rounded-md">
                        <svg
                          className="w-8 md:w-10 h-8 md:h-10 me-3 text-gray-200"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z" />
                        </svg>
                      </div>
                      <div className="h-4 md:h-5 bg-gray-200 rounded-full w-36 md:w-48 mb-3 md:mb-4"></div>
                      <div className="h-3 md:h-4 bg-gray-200 rounded-full mb-2.5"></div>
                      <div className="h-3 md:h-4 bg-gray-200 rounded-full mb-2.5"></div>
                      <div className="h-3 md:h-4 bg-gray-200 rounded-full"></div>
                      <span className="sr-only">Loading...</span>
                    </div>
                  ))}
              </div>
            </>
          ) : error ? (
            <div className="flex justify-center px-4">
              <div className="mt-40 md:mt-60 bg-white max-w-md rounded-lg shadow-md border border-gray-200 p-4 text-center">
                <h2 className="text-lg md:text-xl">You don't have any roles yet!</h2>
                <button
                  onClick={() => {
                    navigate("/new-roles");
                  }}
                  className="px-4 mt-4 text-sm md:text-md py-2 bg-[#02B491] text-white rounded-md hover:bg-white border-2 border-[#02B491] hover:text-black"
                >
                  Create a Role
                </button>
              </div>
            </div>
          ) : Object.keys(data).length === 0 ? (
            <div className="flex justify-center px-4">
              <div className="mt-40 md:mt-60 bg-white max-w-md rounded-lg shadow-md border border-gray-200 p-4 text-center">
                <h2 className="text-lg md:text-xl">
                  You don't have any shortlisted candidates yet!
                </h2>
                <button
                  onClick={() => {
                    navigate("/browse-candidates");
                  }}
                  className="px-4 mt-4 text-sm md:text-md py-2 bg-[#02B491] text-white rounded-md hover:bg-white border-2 border-[#02B491] hover:text-black"
                >
                  Browse Candidates
                </button>
              </div>
            </div>
          ) : (
            Object.keys(data).map((role, index) => (
              <div key={role} className="relative p-2 md:p-4 mb-4 md:mb-6">
                <div className="flex">
                  <h2 className="block text-xl md:text-2xl pt-2 md:pt-4 font-medium leading-6 text-gray-900">
                    {role}
                  </h2>
                </div>

                <div className="">
                  <div className="max-w-full pt-1 mx-auto sm:max-w-none">
                    <div className="text-sm md:text-base text-gray-600">
                      You have {data[role].length} candidates in this shortlist.
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="bg-gray-50 border border-gray-200 p-3 md:p-4 mt-3 rounded-lg flex flex-col shadow-sm">
                        <div className="flex flex-col md:flex-row items-center gap-y-3 md:gap-y-0 md:gap-x-6">
                          <div
                            onClick={() => handleToggleSelectAll(role)}
                            className="flex items-center self-start gap-x-2 cursor-pointer"
                          >
                            <button
                              className={`rounded-md border-2 w-8 h-8 md:w-10 md:h-10 ${
                                areAllCandidatesSelected(role)
                                  ? "bg-[#02B491] border-[#02B491] text-white"
                                  : "border-[#02B491]"
                              }`}
                            >
                              {areAllCandidatesSelected(role) && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="2.5"
                                  stroke="currentColor"
                                  className="w-5 h-5 md:w-6 md:h-6 mx-auto"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </button>
                            <p className="text-base md:text-lg">Select All</p>
                          </div>
                          <div className="w-full md:w-auto">
                            <button
                              onClick={() =>
                                handleRequestInterview(
                                  data[role][0].roleURLId,
                                  role
                                )
                              }
                              disabled={
                                !selectedCandidates[role] ||
                                selectedCandidates[role].length === 0
                              }
                              data-tooltip-target="tooltip-default"
                              type="button"
                              className={`group w-full md:max-w-[300px] px-6 md:px-8 py-2 rounded-md font-medium transition duration-200 border-2 ${
                                selectedCandidates[role]?.length > 0
                                  ? "bg-[#02B491] text-white hover:bg-white hover:text-black border-[#02B491]"
                                  : "bg-gray-300 text-black cursor-not-allowed border-gray-300 hover:bg-gray-300"
                              }`}
                            >
                              <p
                                className={`flex items-center justify-center text-sm md:text-base ${
                                  selectedCandidates[role]?.length > 0
                                    ? "text-white group-hover:text-black"
                                    : "text-black"
                                }`}
                              >
                                Request {selectedCandidates[role]?.length}{" "}
                                Interview
                                {selectedCandidates[role]?.length > 1 && (
                                  <span>s</span>
                                )}
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="1.5"
                                  stroke="currentColor"
                                  className="size-5 md:size-6 ml-2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                                  />
                                </svg>
                              </p>
                            </button>

                            <p className="flex text-black italic font-normal mt-1 text-xs md:text-sm group-hover:text-black">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="size-4 md:size-5 mr-1"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                                />
                              </svg>
                              We'll ask you a few questions first!
                            </p>
                          </div>
                        </div>

                        <hr className="my-3 md:my-4" />

                        <div className="w-full">
                          <button
                            onClick={() => handleRequestMoreRecs(data[role][0].roleId)}
                            disabled={requestingRecs}
                            className="flex items-center text-[#02B491] hover:text-[#029d7a] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              className="size-5 mr-2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
                              />
                            </svg>
                            <span className="text-xs">
                              {requestingRecs
                                ? "Sending..."
                                : "Request recommendations from our team"}
                            </span>
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => handleShortlist(data[role][0].roleURLId)}
                        className="hidden px-4 ml-2 mt-2 justify-center text-sm justify-center py-2 hover:bg-gray-200 rounded-md flex flex-col items-center"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="size-8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                          />
                        </svg>
                        View full shortlist
                      </button>
                    </div>
                    <div className="max-w-full overflow-x-auto mt-4">
                      <div className="flex space-x-4 h-full pb-4">
                        {(() => {
                          // Sort candidates: those with rationales first, then others
                          // Preserve original order within each group
                          const candidatesWithRationales = [];
                          const candidatesWithoutRationales = [];
                          
                          data[role].forEach((candidate, originalIndex) => {
                            const hasRationale = shortlistRationales[candidate.roleId]?.[candidate.id];
                            if (hasRationale) {
                              candidatesWithRationales.push({ ...candidate, originalIndex });
                            } else {
                              candidatesWithoutRationales.push({ ...candidate, originalIndex });
                            }
                          });
                          
                          // Combine: rationales first, then others (both maintaining original order)
                          const sortedCandidates = [...candidatesWithRationales, ...candidatesWithoutRationales];
                          
                          return sortedCandidates.map((candidate, candidateIndex) => (
                            <div
                              key={candidate.originalIndex}
                              className="flex-shrink-0 w-64 md:w-auto h-full"
                            >
                              <ShortlistCard
                                candidate={candidate}
                                handleRemoveCandidate={handleRemoveCandidate}
                                role={role}
                                handleAddSelectedCandidate={
                                  handleAddSelectedCandidate
                                }
                                handleRemoveSelectedCandidate={
                                  handleRemoveSelectedCandidate
                                }
                                selectedCandidates={
                                  selectedCandidates[role] || []
                                }
                                removing={removing}
                                roles={roles}
                                onShortlistCandidate={handleShortlistCandidate}
                                shortlistRationale={shortlistRationales[candidate.roleId]?.[candidate.id] || null}
                              />
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                    {data[role].length > 2 && (
                      <div className="flex mt-4 space-x-3 justify-end pr-4 hidden">
                        <button
                          className={`carousel-prev carousel-prev-${index} absolute top-96 left-0 z-20 w-11 h-11 rounded-full flex items-center justify-center group bg-gray-900`}
                        >
                          <span className="sr-only">Previous</span>
                          <svg
                            className="fill-white group-hover:fill-[#02B491] transition duration-150 ease-in-out"
                            width="13"
                            height="12"
                            viewBox="0 0 13 12"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="m3.914 5 3.5-3.5L6 .086 1.086 5H1v.086L.086 6 1 6.914V7h.086L6 11.914 7.414 10.5 3.914 7H13V5z" />
                          </svg>
                        </button>
                        <button
                          className={`carousel-next carousel-next-${index} absolute top-96 right-0 z-20 w-11 h-11 rounded-full flex items-center justify-center group bg-gray-900`}
                        >
                          <span className="sr-only">Next</span>
                          <svg
                            className="fill-white group-hover:fill-[#02B491] transition duration-150 ease-in-out"
                            width="13"
                            height="12"
                            viewBox="0 0 13 12"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="m9.086 5-3.5-3.5L7 .086 11.914 5H12v.086l.914.914-.914.914V7h-.086L7 11.914 5.586 10.5l3.5-3.5H0V5z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <RequestRecsModal
        isOpen={showRequestRecsModal}
        onClose={() => setShowRequestRecsModal(false)}
        loading={requestingRecs}
      />
      <KeenToSeeMoreModal
        isOpen={showKeenModal}
        onClose={() => setShowKeenModal(false)}
        onConfirm={async () => {
          if (keenModalInfo) {
            await handleRequestMoreRecs(keenModalInfo.roleId);
          }
          setShowKeenModal(false);
        }}
        roleName={keenModalInfo?.roleName}
        loading={requestingRecs}
      />
    </div>
  );
}

export default Shortlists;